'use client';

import { Button, Flex, Input, Table, Text } from '@chakra-ui/react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { LuPlus } from 'react-icons/lu';

import NewCustomerDialog from '@features/finance/NewCustomerDialog';
import { customerBalanceDue, formatMoney, invoicesForCustomer } from '@features/finance/invoiceMeta';
import { allCustomers } from 'helpers/fakeData';
import { useHref } from 'hooks/useHref';

const inputProps = {
  borderColor: 'border.light',
  borderRadius: 'md',
  size: 'sm' as const,
};

const CustomersPage = () => {
  const router = useRouter();
  const { customerHref } = useHref();
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const onSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, []);
  const onOpenDialog = useCallback(() => setDialogOpen(true), []);
  const onDialogOpenChange = useCallback((details: { open: boolean }) => {
    setDialogOpen(details.open);
  }, []);
  const onRowClick = useCallback(
    (id: string) => () => {
      router.push(customerHref(id));
    },
    [router, customerHref],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') {
      return allCustomers;
    }
    return allCustomers.filter(
      (customer) =>
        customer.name.toLowerCase().includes(q) ||
        customer.phone.includes(q) ||
        (customer.email ?? '').toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <Flex flexDirection="column" gap={4} p={4}>
      <Flex justifyContent="space-between" alignItems="center" gap={2}>
        <Flex direction="column" gap={0.5}>
          <Text fontSize="lg" fontWeight="bold">
            Customers
          </Text>
          <Text fontSize="sm" color="text.secondary">
            Everyone you bill or collect from. Tracked by phone number.
          </Text>
        </Flex>
        <Button size="2xs" borderRadius="md" onClick={onOpenDialog}>
          <LuPlus size={14} />
          New customer
        </Button>
      </Flex>
      <Input placeholder="Search by name, phone, or email" value={query} onChange={onSearch} {...inputProps} maxW="sm" />
      <Flex direction="column" bg="surface.muted" borderRadius="xl" px={2} pb={2}>
        <Table.Root>
          <Table.Header fontSize="sm" fontWeight="medium">
            <Table.Row bg="transparent">
              <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4}>
                Name
              </Table.ColumnHeader>
              <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4} hideBelow="md">
                Phone
              </Table.ColumnHeader>
              <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4} hideBelow="md">
                Email
              </Table.ColumnHeader>
              <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4} textAlign="end">
                Invoices
              </Table.ColumnHeader>
              <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4} textAlign="end">
                Balance due
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered.map((customer, index) => (
              <Table.Row key={customer.id} fontSize="sm" cursor="pointer" onClick={onRowClick(customer.id)}>
                <Table.Cell
                  border="none"
                  p={4}
                  borderTopLeftRadius={index === 0 ? 'xl' : 'none'}
                  borderBottomLeftRadius={index === filtered.length - 1 ? 'xl' : 'none'}
                >
                  <Text fontWeight="medium">{customer.name}</Text>
                </Table.Cell>
                <Table.Cell border="none" p={4} hideBelow="md" fontFamily="mono">
                  {customer.phone}
                </Table.Cell>
                <Table.Cell border="none" p={4} hideBelow="md" color="text.secondary">
                  {customer.email ?? '—'}
                </Table.Cell>
                <Table.Cell border="none" p={4} textAlign="end">
                  {invoicesForCustomer(customer.id).length}
                </Table.Cell>
                <Table.Cell
                  border="none"
                  p={4}
                  textAlign="end"
                  fontWeight="medium"
                  borderTopRightRadius={index === 0 ? 'xl' : 'none'}
                  borderBottomRightRadius={index === filtered.length - 1 ? 'xl' : 'none'}
                >
                  GHS {formatMoney(customerBalanceDue(customer.id))}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Flex>
      <NewCustomerDialog open={dialogOpen} onOpenChange={onDialogOpenChange} />
    </Flex>
  );
};

export default memo(CustomersPage);
